import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Table, Button, Input, Tag, Space, Modal, Tooltip, Select, Descriptions } from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  SearchOutlined, EyeOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { getUsers, deleteUser, blockAuser, resetState } from "../features/cutomers/customerSlice";

const { Option } = Select;

const roleColors = {
  admin: "purple", vc: "blue", vt: "green",
  hr: "orange", accountant: "gold", subadmin: "cyan",
};

const UserManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [viewUser, setViewUser] = useState(null);

  const { customers: users, isLoading } = useSelector((state) => state.customer);

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  const handleDelete = (user) => {    Modal.confirm({
      title: `Delete "${user.name}"?`,
      content: "This action cannot be undone.",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        const res = await dispatch(deleteUser(user._id));
        if (res.meta.requestStatus === "fulfilled") {
          toast.success("User deleted successfully");
          dispatch(getUsers());
        } else {
          toast.error("Failed to delete user");
        }
      },
    });
  };

  const handleBlock = (user) => {
    setViewUser(user);
  };

  const filtered = users.filter((u) => {
    const matchSearch =
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.unique_id?.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === "all" || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const columns = [
    {
      title: "#",
      render: (_, __, i) => i + 1,
      width: 50,
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => a.name?.localeCompare(b.name),
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Phone",
      dataIndex: "phone_no",
    },
    {
      title: "Role",
      dataIndex: "role",
      render: (role) => (
        <Tag color={roleColors[role] || "default"} style={{ textTransform: "capitalize" }}>
          {role}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "isBlocked",
      render: (blocked) =>
        blocked ? <Tag color="red">Blocked</Tag> : <Tag color="green">Active</Tag>,
    },
    {
      title: "State",
      dataIndex: "state_name",
      render: (v) => v || "—",
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              type="primary"
              ghost
              onClick={() => navigate(`/admin/vtvc/${record._id}`)}
            />
          </Tooltip>
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              size="small"
              style={{ color: "#2980b9", borderColor: "#2980b9" }}
              onClick={() => handleBlock(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 style={{ margin: 0, fontWeight: 700, color: "#1a1a2e" }}>User Management</h5>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/admin/vtvc")}
          style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
        >
          Add User
        </Button>
      </div>

      {/* Stat Cards */}
      <div className="d-flex gap-3 flex-wrap mb-4">
        {[
          { label: "Total Users", value: users.length, color: "#2980b9" },
          { label: "Active", value: users.filter((u) => !u.isBlocked).length, color: "#27ae60" },
          { label: "Blocked", value: users.filter((u) => u.isBlocked).length, color: "#c0392b" },
          { label: "VT", value: users.filter((u) => u.role === "vt").length, color: "#e67e22" },
          { label: "VC", value: users.filter((u) => u.role === "vc").length, color: "#8e44ad" },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              background: s.color, color: "#fff", borderRadius: 10,
              padding: "14px 22px", minWidth: 110, boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ fontSize: 11, opacity: 0.85, textTransform: "uppercase" }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by name, email, ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
          allowClear
        />
        <Select value={roleFilter} onChange={setRoleFilter} style={{ width: 140 }}>
          <Option value="all">All Roles</Option>
          <Option value="vc">VC</Option>
          <Option value="vt">VT</Option>
          <Option value="hr">HR</Option>
          <Option value="accountant">Accountant</Option>
          <Option value="subadmin">Sub Admin</Option>
        </Select>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={filtered}
        rowKey="_id"
        loading={isLoading}
        size="small"
        pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (t) => `Total ${t} users` }}
        scroll={{ x: 900 }}
        bordered
      />

      {/* View User Modal */}
      <Modal
        title={viewUser?.name}
        open={!!viewUser}
        onCancel={() => setViewUser(null)}
        footer={<Button onClick={() => setViewUser(null)}>Close</Button>}
        width={600}
        centered
      >
        {viewUser && (
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="Name">{viewUser.name || "—"}</Descriptions.Item>
            <Descriptions.Item label="Unique ID">{viewUser.unique_id || "—"}</Descriptions.Item>
            <Descriptions.Item label="Email">{viewUser.email || "—"}</Descriptions.Item>
            <Descriptions.Item label="Phone">{viewUser.phone_no || "—"}</Descriptions.Item>
            <Descriptions.Item label="Role">
              <Tag color={roleColors[viewUser.role] || "default"} style={{ textTransform: "capitalize" }}>
                {viewUser.role}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {viewUser.isBlocked ? <Tag color="red">Blocked</Tag> : <Tag color="green">Active</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Qualification">{viewUser.qualification || "—"}</Descriptions.Item>
            <Descriptions.Item label="Experience">{viewUser.experience || "—"}</Descriptions.Item>
            <Descriptions.Item label="Skill Area">{viewUser.skill_area || "—"}</Descriptions.Item>
            <Descriptions.Item label="Job Role">{viewUser.job_role || "—"}</Descriptions.Item>
            <Descriptions.Item label="State">{viewUser.state_name || viewUser.state || "—"}</Descriptions.Item>
            <Descriptions.Item label="District">{viewUser.district || "—"}</Descriptions.Item>
            <Descriptions.Item label="School">{viewUser.school_name || "—"}</Descriptions.Item>
            <Descriptions.Item label="Address" span={2}>{viewUser.address || "—"}</Descriptions.Item>
            <Descriptions.Item label="Created">
              {viewUser.createdAt ? new Date(viewUser.createdAt).toLocaleDateString("en-IN") : "—"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default UserManagement;
